import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { IReorderSettingsRow, ReorderSettingsItemType } from 'src/types/reorder-settings';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  reorderSettings: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IReorderSettingsRow[];
  };
  // "My Tasks" — admins see every low/out-of-stock item, everyone else
  // only their own orderBy assignments (the branch lives server-side).
  // Unpaginated (a short, actionable list), kept separate from the main
  // reorderSettings table state above so the two pages never clobber
  // each other.
  myTasksStatus: 'idle' | 'loading' | 'success' | 'failure';
  myTasks: IReorderSettingsRow[];
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  reorderSettings: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  myTasksStatus: 'idle',
  myTasks: [],
};

// Maps each item type to the controller that owns the actual entity —
// mutation logic + audit trail stay colocated with the entity, so there's
// no single "reorder settings" write endpoint, only this one read-side
// aggregator (see ReorderSettingsService on the backend).
const REORDER_SETTINGS_ENDPOINT_BY_TYPE: Record<ReorderSettingsItemType, string> = {
  inventory: 'inventory/item',
  packaging: 'packaging/item',
  stationery: 'stationery/item',
};

const reorderSettingsSlice = createSlice({
  name: 'reorderSettings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getReorderSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getReorderSettings.fulfilled, (state, action) => {
        state.status = 'success';
        state.reorderSettings = action.payload;
        state.error = initialState.error;
      })
      .addCase(getReorderSettings.rejected, (state, action) => {
        state.reorderSettings = initialState.reorderSettings;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(updateReorderSettingsRow.fulfilled, (state, action) => {
        const { id, updated } = action.payload;

        const index = state.reorderSettings.items.findIndex((item) => item._id === id);
        if (index > -1) {
          state.reorderSettings.items[index] = {
            ...state.reorderSettings.items[index],
            itemCode: updated.itemCode,
            itemName: updated.itemName,
            quantity: updated.quantity,
            reorderThreshold: updated.reorderThreshold,
            newOrderQty: updated.newOrderQty ?? null,
            hiddenFromMyTasks: updated.hiddenFromMyTasks ?? false,
            orderBy: updated.orderBy ?? null,
          };
        }
      })
      .addCase(getMyTasks.pending, (state) => {
        state.myTasksStatus = 'loading';
      })
      .addCase(getMyTasks.fulfilled, (state, action) => {
        state.myTasksStatus = 'success';
        state.myTasks = action.payload;
      })
      .addCase(getMyTasks.rejected, (state, action) => {
        state.myTasks = initialState.myTasks;
        state.myTasksStatus = 'failure';
        state.error = action.error.message;
      });
  },
});

export default reorderSettingsSlice.reducer;

export const getReorderSettings = createAsyncThunk(
  'reorderSettings/paginated',
  async (params: {
    page: number;
    limit: number;
    type: 'all' | ReorderSettingsItemType;
    generalSearch?: string | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, type, generalSearch, sortBy, sort } = params;

    try {
      const response = await axios.get(
        `reorder-settings?page=${page + 1}&limit=${limit}&type=${type}&generalSearch=${
          generalSearch ?? ''
        }&sortBy=${sortBy ?? ''}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getMyTasks = createAsyncThunk('reorderSettings/myTasks', async () => {
  try {
    const response = await axios.get('reorder-settings/my-tasks');

    return response.data.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const updateReorderSettingsRow = createAsyncThunk(
  'reorderSettings/updateRow',
  async (params: {
    itemType: ReorderSettingsItemType;
    id: string;
    orderBy?: string;
    newOrderQty?: number;
    hiddenFromMyTasks?: boolean;
  }) => {
    const { itemType, id, ...body } = params;

    try {
      const response = await axios.patch(
        `${REORDER_SETTINGS_ENDPOINT_BY_TYPE[itemType]}/${id}/reorder-settings`,
        body
      );

      return { id, updated: response.data.data };
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);
